/**
 * Service for handling antibody table import and specification generation
 */

import { clerkService } from './clerkService';

const API_BASE_URL = import.meta.env?.VITE_API_URL || 'http://localhost:5001';

/**
 * Upload and parse an antibody table file
 * @param {File} file - The CSV or Excel file to upload
 * @returns {Promise<Object>} Parsed table data and generated specification
 */
export async function uploadAntibodyTable(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
        // Get auth token
        const token = await clerkService.getToken();
        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        // Let browser set Content-Type with boundary for multipart/form-data
        
        const response = await fetch(`${API_BASE_URL}/api/antibody/upload`, {
            method: 'POST',
            body: formData,
            credentials: 'include',
            headers: headers
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to upload antibody table');
        }
        
        return await response.json();
    } catch (error) {
        console.error('Error uploading antibody table:', error);
        throw error;
    }
}

/**
 * Generate all specification paragraphs from antibody table data
 * @param {Array} tableData - Array of antibody data objects
 * @returns {Promise<Object>} Generated specification paragraphs
 */
export async function generateAllAntibodyParagraphs(tableData) {
    try {
        // Get auth token
        const token = await clerkService.getToken();
        const headers = {
            'Content-Type': 'application/json'
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API_BASE_URL}/api/antibody/generate-all-paragraphs`, {
            method: 'POST',
            headers: headers,
            credentials: 'include',
            body: JSON.stringify({ table_data: tableData })
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to generate specification paragraphs');
        }
        
        return await response.json();
    } catch (error) {
        console.error('Error generating antibody paragraphs:', error);
        throw error;
    }
}

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export async function copyToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        // Fallback method
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            document.body.removeChild(textArea);
            return true;
        } catch (err) {
            document.body.removeChild(textArea);
            return false;
        }
    }
}

/**
 * Format antibody table data for display
 * @param {Array} tableData - Raw table data from API
 * @returns {Array} Formatted table data
 */
export function formatAntibodyTableData(tableData) {
    return tableData.map(row => ({
        name: row.Ab_Name,
        vh: row.VH ? `SEQ ID NO: ${row.VH}` : '-',
        vl: row.VL ? `SEQ ID NO: ${row.VL}` : '-',
        cdrH1: row['CDR-H1'] ? `SEQ ID NO: ${row['CDR-H1']}` : '-',
        cdrH2: row['CDR-H2'] ? `SEQ ID NO: ${row['CDR-H2']}` : '-',
        cdrH3: row['CDR-H3'] ? `SEQ ID NO: ${row['CDR-H3']}` : '-',
        cdrL1: row['CDR-L1'] ? `SEQ ID NO: ${row['CDR-L1']}` : '-',
        cdrL2: row['CDR-L2'] ? `SEQ ID NO: ${row['CDR-L2']}` : '-',
        cdrL3: row['CDR-L3'] ? `SEQ ID NO: ${row['CDR-L3']}` : '-'
    }));
}

/**
 * Convert formatted table data to HTML table for insertion
 * @param {Array} formattedData - Formatted antibody table data
 * @returns {string} HTML table string
 */
export function generateAntibodyTableHTML(formattedData) {
    let html = '<table class="antibody-table">';
    html += '<thead><tr>';
    html += '<th>Antibody Name</th>';
    html += '<th>VH</th><th>VL</th>';
    html += '<th>CDR-H1</th><th>CDR-H2</th><th>CDR-H3</th>';
    html += '<th>CDR-L1</th><th>CDR-L2</th><th>CDR-L3</th>';
    html += '</tr></thead>';
    html += '<tbody>';
    
    formattedData.forEach(row => {
        html += '<tr>';
        html += `<td>${row.name}</td>`;
        html += `<td>${row.vh}</td>`;
        html += `<td>${row.vl}</td>`;
        html += `<td>${row.cdrH1}</td>`;
        html += `<td>${row.cdrH2}</td>`;
        html += `<td>${row.cdrH3}</td>`;
        html += `<td>${row.cdrL1}</td>`;
        html += `<td>${row.cdrL2}</td>`;
        html += `<td>${row.cdrL3}</td>`;
        html += '</tr>';
    });
    
    html += '</tbody></table>';
    return html;
}